import { Modal, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import { MaterialTableColumns } from "../../../../../../configs/provider";
import { Material } from "../../../../../../data/entities";
import { StyledBackdrop } from "../../../../../../shared/components/CancelModal/Styled";
import CustomTable from "../../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../../../shared/components/IconButton";
import SearchBarWithSuggestion from "../../../../../../shared/components/SearchBar/SearchBarWithSuggestion";
import TextBox from "../../../../../../shared/components/TextBox";
import CustomButton from "../../../../../../shared/components/TitleRow/CustomButton";
import { api } from "../../../../../../shared/hooks/api";
import DeleteIcon from "../../../../../../shared/svg/DeleteIcon";
import { formatInternationalNumber, parseInternationalToNumber } from "../../../../../../utils/format";
import { error } from "../../../../../../utils/toast";
import OutsourceMaterialSearchBar from "../OutsourceMaterialSearchBar";
import { ButtonRow, Expandable, InfoText, ModalWrapper, TableScrollArea, TitleText, TitleWrapper } from "./Styled";

interface MaterialRowProps {
    item: any;
    extraRowProps: any;
}

const MaterialRow = ({ item, extraRowProps }: MaterialRowProps) => {
    const price = item.providerPrice ? item.providerPrice : 0;
    const note = item.note !== undefined ? item.note : "";

    return (
        <TableRow>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            <StyledTableCell align="left">
                {item.measurement_unit}
            </StyledTableCell>
            <StyledTableCell align="left">
                <TextBox
                    numeric
                    text={formatInternationalNumber(price ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialPrice(item, n);
                    }}
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <TextBox
                    text={note}
                    onChangeText={(value: string) =>
                        extraRowProps.onChangeMaterialNote(item, value)
                    }
                />
            </StyledTableCell>
            <StyledTableCell align="left">
                <IconButton onClick={() => extraRowProps.removeItem(item)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

interface AddMaterialModalProps {
    selectedMaterials: Material[];
    open: boolean;
    handleClose: any;
    mergeContactPerson: Function;
    relationKey?: "materials" | "outsource_materials";
}

const AddMaterialModal = ({
                              selectedMaterials,
                              open,
                              handleClose,
                              mergeContactPerson,
                              relationKey = "materials",
                          }: AddMaterialModalProps) => {
    const [isOpen, setIsOpen] = useState(open);
    const [{ data }, fetchMaterials] = api.get("/materials/", {
        option: relationKey
    });
    const [selectedItems, setSelectedItems] =
        useState<Material[]>(selectedMaterials);
    const materials = data && data.results ? data.results : [];
    const totalItems = data && data.count ? data.count : 0;
    const isOutsourceMaterialList = relationKey === "outsource_materials";
    const title = isOutsourceMaterialList ? "Thêm vật tư gia công" : "Thêm vật tư";
    const infoText = isOutsourceMaterialList ? "Vui lòng lựa chọn vật tư gia công" : "Vui lòng lựa chọn vật tư";
    const saveButtonLabel = isOutsourceMaterialList ? "Lưu vật tư gia công" : "Lưu vật tư";
    const tableColumns = isOutsourceMaterialList
        ? MaterialTableColumns.map((column) =>
            column.text === "Giá cung cấp" ? { ...column, text: "Giá gia công" } : column
        )
        : MaterialTableColumns;

    const tableItems = selectedItems.length === 0 ? [] : [...selectedItems];

    const onRemoveMaterial = (removedMaterial: Material) => {
        const removedMaterials = selectedItems.filter(
            (material) => material.id !== removedMaterial.id,
        );
        setSelectedItems(removedMaterials);
    };

    const onUpdateMaterialPrice = (material: Material, price: number) => {
        const updatedMaterials = selectedItems.map((item) => {
            if (item.id === material.id)
                return { ...item, providerPrice: price };
            return item;
        });
        setSelectedItems(updatedMaterials);
    };

    const handleOnClickItem = (item: Material, isAdded: boolean) => {
        const isItemContained = selectedItems.some(
            (selectedItem) => selectedItem.name === item.name,
        );
        if (isAdded && !isItemContained) {
            setSelectedItems([...selectedItems, item]);
        } else if (!isAdded && isItemContained) {
            onRemoveMaterial(item);
        }
    };

    const onUpdateMaterialNote = (material: Material, note: string) => {
        const updatedMaterials = selectedItems.map((item) => {
            if (item.id === material.id) return { ...item, note };
            return item;
        });
        setSelectedItems(updatedMaterials);
    };

    const onClickAddMaterials = () => {
        mergeContactPerson({ [relationKey]: selectedItems });
        handleClose();
    };

    const handleCloseWithAnimation = () => {
        setIsOpen(false);
        setTimeout(handleClose, 200);
    };

    useEffect(() => {
        if (open) {
            setIsOpen(true);
        } else {
            const timer = setTimeout(() => setIsOpen(false), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    return (
        <Modal
            open={isOpen}
            onClose={handleCloseWithAnimation}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
        >
            <ModalWrapper isOpen={isOpen}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>{title}</TitleText>
                        <InfoText>{infoText}</InfoText>
                    </TitleWrapper>

                    {isOutsourceMaterialList && (
                        <OutsourceMaterialSearchBar
                            menuList={materials}
                            selectedItems={selectedItems}
                            fetchItems={(variables: any) =>
                                fetchMaterials({
                                    ...variables,
                                    page_size: Math.max(totalItems, 50),
                                })
                            }
                            handleOnclickItem={handleOnClickItem}
                        />
                    )}

                    {!isOutsourceMaterialList && (
                    <SearchBarWithSuggestion
                        menuList={materials}
                        selectedItems={selectedItems}
                        fetchItems={(variables: any) =>
                            fetchMaterials({
                                ...variables,
                                page_size: Math.max(totalItems, 50),
                            })
                        }
                        handleOnclickItem={handleOnClickItem}
                        placeholder="Tìm kiếm theo từ khoá Mã vật tư, Tên vật tư"
                        disableAutoScroll
                    />
                    )}

                    <TableScrollArea>
                        <CustomTable
                            itemList={tableItems}
                            RowElement={MaterialRow}
                            TableColumns={tableColumns}
                            extraRowProps={{
                                removeItem: onRemoveMaterial,
                                onChangeMaterialPrice: onUpdateMaterialPrice,
                                onChangeMaterialNote: onUpdateMaterialNote,
                            }}
                        />
                    </TableScrollArea>
                </Expandable>

                <ButtonRow>
                    <CustomButton
                        title="Huỷ Bỏ"
                        onClick={handleCloseWithAnimation}
                    />
                    <CustomButton
                        title={saveButtonLabel}
                        variant="contained"
                        onClick={onClickAddMaterials}
                    />
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};

export default AddMaterialModal;
